import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { LabeledSelectBox } from '@/components/ui';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import type { Company } from '@packages/types/company';
import type { SiteResponse } from '@packages/types/site/dto';
import type { Device } from '@packages/types/device/dto';

const Container = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]}px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 280px;
  min-height: 42px;
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${({ theme }) => theme.colors.border.container};
  border-top-color: ${({ theme }) => theme.colors.border.indicator};
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export type SelectionDepth = 'company' | 'site' | 'device';

interface HierarchicalSelectorProps {
  // 선택 깊이 설정
  depth: SelectionDepth;

  // Company 관련
  companies?: Company[];
  loadingCompanies?: boolean;
  selectedCompanyId?: number | 'all' | '' | null;
  onCompanyChange?: (companyId: number | 'all' | null) => void;
  companyDisabled?: boolean;
  showCompanyAll?: boolean; // 전체 조회 가능 여부 (true: "All Companies" 옵션, false: "Select" 옵션)

  // Site 관련
  sites?: SiteResponse[];
  selectedSiteId?: number | 'all' | '' | null;
  onSiteChange?: (siteId: number | 'all' | null) => void;
  siteDisabled?: boolean;
  loadingSites?: boolean;
  showSiteAll?: boolean; // 전체 조회 가능 여부 (true: "All Sites" 옵션, false: "Select" 옵션)

  // Device 관련
  devices?: Device[];
  selectedDeviceId?: number | 'all' | '' | null;
  onDeviceChange?: (deviceId: number | 'all' | null) => void;
  deviceDisabled?: boolean;
  loadingDevices?: boolean;
  showDeviceAll?: boolean; // 전체 조회 가능 여부 (true: "All Devices" 옵션, false: "Select" 옵션)

  // 스타일링
  gap?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * HierarchicalSelector
 *
 * Company > Site > Device 계층 구조를 가진 선택 컴포넌트
 *
 * 전체 조회 옵션:
 * - showCompanyAll/showSiteAll/showDeviceAll이 true인 경우: "All" 옵션 표시
 * - false인 경우 (기본값): "Select" 옵션 표시
 *
 * 사용 예시:
 * 1. Company만 선택 (Select 옵션 포함, 기본값):
 *    <HierarchicalSelector depth="company" companies={companies} selectedCompanyId={id} onCompanyChange={handler} />
 *
 * 2. Company 전체 조회 가능:
 *    <HierarchicalSelector depth="company" companies={companies} showCompanyAll={true} ... />
 *
 * 3. Company + Site 선택 (Site는 Select 옵션):
 *    <HierarchicalSelector depth="site" companies={companies} sites={sites} ... />
 *
 * 4. Company + Site + Device 선택 (모두 전체 조회 가능):
 *    <HierarchicalSelector depth="device" companies={companies} sites={sites} devices={devices} showCompanyAll={true} showSiteAll={true} showDeviceAll={true} ... />
 *
 * 5. Company는 고정값, Site만 선택:
 *    <HierarchicalSelector depth="site" companies={[fixedCompany]} selectedCompanyId={fixedId} companyDisabled={true} ... />
 */
export const HierarchicalSelector: React.FC<HierarchicalSelectorProps> = ({
  depth,
  companies = [],
  loadingCompanies = false,
  selectedCompanyId,
  onCompanyChange,
  companyDisabled = false,
  showCompanyAll = false,
  sites = [],
  selectedSiteId,
  onSiteChange,
  siteDisabled = false,
  loadingSites = false,
  showSiteAll = false,
  devices = [],
  selectedDeviceId,
  onDeviceChange,
  deviceDisabled = false,
  loadingDevices = false,
  showDeviceAll = false,
}) => {
  const { t } = useTranslation();

  const showCompany = depth === 'company' || depth === 'site' || depth === 'device';
  const showSite = depth === 'site' || depth === 'device';
  const showDevice = depth === 'device';

  // Company 자동 선택: companies가 있고 showCompanyAll이 false이고 selectedCompanyId가 null/''일 때 첫 번째 회사 자동 선택
  useEffect(() => {
    if (
      showCompany &&
      companies.length > 0 &&
      !showCompanyAll &&
      (selectedCompanyId === null || selectedCompanyId === '')
    ) {
      // 첫 번째 회사를 자동 선택
      onCompanyChange?.(companies[0].id);
    }
  }, [showCompany, companies, showCompanyAll, selectedCompanyId, onCompanyChange]);

  // Company options
  // showCompanyAll이 false이고 companies가 있을 때는 "Select" 옵션을 표시하지 않음 (첫 번째 회사가 자동 선택됨)
  const companyOptions = useMemo(() => {
    if (companies.length === 0) {
      return [{ label: t(i18nKeys.site.noSelectOptions), value: '' }];
    }
    const options = companies.map(company => ({
      label: company.name,
      value: company.id,
    }));
    if (showCompanyAll) {
      return [{ label: t(i18nKeys.site.allCompanies), value: 'all' }, ...options];
    }
    return options;
  }, [companies, showCompanyAll, t]);

  // Site options
  const siteOptions = useMemo(() => {
    if (sites.length === 0) {
      return [{ label: t(i18nKeys.site.noSelectOptions), value: '' }];
    }
    const options = sites.map(site => ({
      label: site.name,
      value: site.id,
    }));
    if (showSiteAll) {
      return [{ label: t(i18nKeys.site.allSites), value: 'all' }, ...options];
    }
    return [{ label: t(i18nKeys.site.selectSite), value: '' }, ...options];
  }, [sites, showSiteAll, t]);

  // Device options
  const deviceOptions = useMemo(() => {
    if (devices.length === 0) {
      return [{ label: t(i18nKeys.site.noSelectOptions), value: '' }];
    }
    const options = devices.map(device => ({
      label: device.serial_number,
      value: device.id,
    }));
    if (showDeviceAll) {
      return [{ label: t(i18nKeys.site.allDevices), value: 'all' }, ...options];
    }
    return [{ label: t(i18nKeys.site.selectDevice), value: '' }, ...options];
  }, [devices, showDeviceAll, t]);

  return (
    <Container>
      {/* Company Selector — 로딩/빈 목록이어도 영역 표시(비활성) */}
      {showCompany && (
        <>
          {loadingCompanies ? (
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          ) : (
            <LabeledSelectBox
              label={i18nKeys.company.company}
              value={selectedCompanyId ?? ''}
              options={companyOptions}
              onChange={option => {
                const value = option.value;
                if (value === '') {
                  onCompanyChange?.(null);
                } else {
                  onCompanyChange?.(
                    value === 'all' ? 'all' : typeof value === 'number' ? value : Number(value)
                  );
                }
              }}
              isEdit={!companyDisabled && companies.length > 0}
              width="180px"
              align="center"
              size="md"
              labelSize="sm"
              labelWeight="semibold"
            />
          )}
        </>
      )}

      {/* Site Selector — 빈 목록이어도 회사와 동일하게 비활성 셀렉트박스 */}
      {showSite && (
        <>
          {loadingSites ? (
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          ) : (
            <LabeledSelectBox
              label={i18nKeys.site.site}
              value={selectedSiteId ?? ''}
              options={siteOptions}
              onChange={option => {
                const value = option.value;
                if (value === '') {
                  onSiteChange?.(null);
                } else {
                  onSiteChange?.(
                    value === 'all' ? 'all' : typeof value === 'number' ? value : Number(value)
                  );
                }
              }}
              isEdit={!siteDisabled && sites.length > 0}
              width="180px"
              align="center"
              size="md"
              labelSize="sm"
              labelWeight="semibold"
            />
          )}
        </>
      )}

      {/* Device Selector — 빈 목록이어도 동일 패턴 */}
      {showDevice && (
        <>
          {loadingDevices ? (
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          ) : (
            <LabeledSelectBox
              label={i18nKeys.site.device}
              value={selectedDeviceId ?? ''}
              options={deviceOptions}
              onChange={option => {
                const value = option.value;
                if (value === '') {
                  onDeviceChange?.(null);
                } else {
                  onDeviceChange?.(
                    value === 'all' ? 'all' : typeof value === 'number' ? value : Number(value)
                  );
                }
              }}
              isEdit={!deviceDisabled && devices.length > 0}
              width="180px"
              align="center"
              size="md"
              labelSize="sm"
              labelWeight="semibold"
            />
          )}
        </>
      )}
    </Container>
  );
};
